import { CONTRACT_TERMS_ENDPOINT } from "../../../../../consts";
import { useCRUD } from "../../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ContractTerm, ContractTermPrice } from "../../../../../types/contracts";
import { Box, Typography } from "@mui/material";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../../components/ui/breadcrumps/breadcrumbsContext";
import { EquipmentCounter } from "../../../../../types/equipments";
import { formatCounterValue, formatNumber } from "../../../../../utils";
import {
  getContractTermDisplayLabel,
  groupContractTermPricesByEquipment,
  isCommonCounterContractTerm,
} from "../../../../../utils/settlementContractTerms";
import EntityReferenceLink from "../../../../../components/ui/entityReference/entityReferenceLink.component";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../../../components/ui/detailView";
import { ServiceReminderPlanSummary } from "../../serviceReminderPlanSummary/serviceReminderPlanSummary.component";

const ONE_OFF_SETTLEMENT_CYCLE = "O";

const priceKindLabel = (price: ContractTermPrice) =>
  (price.start_counter as EquipmentCounter)?.kind_display;

const PriceRows = ({
  price,
  t,
  showIncludedInRent,
}: {
  price: ContractTermPrice;
  t: (key: string) => string;
  showIncludedInRent: boolean;
}) => (
  <>
    <DetailRow label={t("Kind")} value={priceKindLabel(price)} />
    <DetailRow
      label={t("Start Counter")}
      value={formatCounterValue((price.start_counter as EquipmentCounter)?.value)}
    />
    {price.end_counter && (
      <DetailRow
        label={t("End Counter")}
        value={
          typeof price.end_counter === "string"
            ? formatCounterValue(price.end_counter)
            : formatCounterValue((price.end_counter as EquipmentCounter)?.value)
        }
      />
    )}
    <DetailRow label={t("Net price")} value={price.price && `${formatNumber(price.price)} zł`} />
    {showIncludedInRent && (
      <DetailRow label={t("Included in Rent")} value={formatCounterValue(price.included_in_rent)} />
    )}
  </>
);

export const ContractTermDetail = () => {
  const { id, termId } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CONTRACT_TERMS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [contractTerm, setContractTerm] = useState<ContractTerm | null>(null);

  useEffect(() => {
    const fetchContractTerm = async () => {
      if (id && termId) {
        const response = await get(termId);
        const breadcrumbPath = `/contracts/${id}/equipment/${termId}`;
        setOverride(
          breadcrumbPath,
          response.data
            ? getContractTermDisplayLabel(response.data, t, String(response.data.id))
            : ""
        );
        setContractTerm(response.data);
      }
    };

    const handleContractTermUpdated = () => {
      fetchContractTerm();
    };

    window.addEventListener("contractTermUpdated", handleContractTermUpdated);
    fetchContractTerm();
    return () => {
      window.removeEventListener("contractTermUpdated", handleContractTermUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contractTerm) {
    return <DetailPage loading />;
  }

  const displayLabel = getContractTermDisplayLabel(
    contractTerm,
    t,
    t("Contract Position")
  );
  const isCommonCounter = isCommonCounterContractTerm(contractTerm);
  const priceGroups = groupContractTermPricesByEquipment(contractTerm);
  const showIncludedInRent = contractTerm.settlement_kind !== "RA";
  // A sale settles once, for the equipment price: no grouping kind, cycle,
  // next date, rent or usage prices apply to it.
  const isSaleTerm = contractTerm.settlement_cycle === ONE_OFF_SETTLEMENT_CYCLE;
  const priceSuffix = contractTerm.contract_info?.is_brutto ? t("Gross") : t("Net");

  return (
    <DetailPage>
      <DetailHeader
        icon={<EventRepeatOutlinedIcon />}
        title={displayLabel}
        subtitle={[contractTerm.kind_display, contractTerm.settlement_cycle_display].filter(Boolean).join(" · ")}
      />

      <DetailSection title={t("Basic Information")}>
        {(contractTerm.equipments?.length ?? 0) > 0 && (
          <DetailRow label={t("Equipment")}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {contractTerm.equipments?.map((equipment) => (
                <EntityReferenceLink
                  key={equipment.id}
                  entityType="equipment"
                  entityId={equipment.id}
                  label={equipment.full_name || "—"}
                  canPreview={true}
                  stopPropagation={false}
                />
              ))}
            </Box>
          </DetailRow>
        )}
        {isSaleTerm ? (
          <>
            <DetailRow label={t("Settlement kind")} value={contractTerm.settlement_kind_display} />
            <DetailRow
              label={t("Equipment price")}
              value={
                contractTerm.equipment_price != null && contractTerm.equipment_price !== ""
                  ? `${formatNumber(contractTerm.equipment_price)} zł (${priceSuffix})`
                  : null
              }
            />
            <DetailRow label={t("Warranty end date")} value={contractTerm.warranty_end_date} />
          </>
        ) : (
          <>
            <DetailRow label={t("Kind")} value={contractTerm.kind_display} />
            <DetailRow label={t("Settlement Cycle")} value={contractTerm.settlement_cycle_display} />
            <DetailRow label={t("Settlement kind")} value={contractTerm.settlement_kind_display} />
            <DetailRow label={t("Next Settlement Date")} value={contractTerm.next_settlement_date} />
            <DetailRow label={t("Net rent price")} value={`${formatNumber(contractTerm.rent_price)} zł`} />
          </>
        )}
      </DetailSection>

      {isSaleTerm && (
        <DetailSection title={t("Obligatory service reminders")} padded>
          <ServiceReminderPlanSummary plan={contractTerm.service_reminder_plan} />
        </DetailSection>
      )}

      {!isSaleTerm && contractTerm.settlement_kind !== "RR" && (
        <>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mt: 1 }}>
            {t("Prices")}
          </Typography>
          <DetailGrid>
            {isCommonCounter
              ? priceGroups.flatMap((group) =>
                  group.prices.map((price, index) => (
                    <DetailSection
                      key={`${group.equipmentId}-${index}`}
                      title={group.equipmentName}
                      description={priceKindLabel(price)}
                    >
                      <PriceRows price={price} t={t} showIncludedInRent={showIncludedInRent} />
                    </DetailSection>
                  ))
                )
              : contractTerm.prices?.map((price, index) => (
                  <DetailSection key={index} title={priceKindLabel(price) || t("Prices")}>
                    <PriceRows price={price} t={t} showIncludedInRent={showIncludedInRent} />
                  </DetailSection>
                ))}
          </DetailGrid>
        </>
      )}

      <DetailMetadata createdAt={contractTerm.created_at} updatedAt={contractTerm.updated_at} />
    </DetailPage>
  );
};
